/**
 * Product Setup Wizard - KI-gestützter Kategorie-Konfigurator
 * 
 * Features:
 * - Kategorie für Kategorie durchgehen
 * - Intelligente Fragen basierend auf Gastro-Wissen
 * - Chat-Interface für natürliche Sprache
 * - Quick-Actions für häufige Optionen
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Send, Wand2, ChevronRight, ChevronLeft,
  CheckCircle, Package, Loader2, Sparkles, X, Plus,
  Coffee, Pizza, Salad, Sandwich, IceCream, Wine,
  ShoppingBag, ArrowRight, RotateCcw, Check
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Kategorie-Icons
const getCategoryIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes('burger')) return Sandwich;
  if (n.includes('pizza')) return Pizza;
  if (n.includes('salat') || n.includes('salad')) return Salad;
  if (n.includes('dessert') || n.includes('eis')) return IceCream;
  if (n.includes('getränk') || n.includes('drink')) return Wine;
  if (n.includes('kaffee') || n.includes('coffee')) return Coffee;
  return Package;
};

export default function ProductSetupWizard({ 
  extractionId, 
  categories, 
  onComplete, 
  onCancel 
}) {
  const [wizardSessionId, setWizardSessionId] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [quickActions, setQuickActions] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [categoryConfigs, setCategoryConfigs] = useState({});
  const [status, setStatus] = useState('idle');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const sessionToken = localStorage.getItem('access_token');

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start Wizard
  const startWizard = useCallback(async () => {
    setIsStarting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/menu-ai/category-wizard/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ extraction_id: extractionId })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Wizard start error:', errorData);
        throw new Error(errorData.detail || 'Wizard konnte nicht gestartet werden');
      }
      
      const data = await response.json();
      
      setWizardSessionId(data.wizard_session_id);
      setCurrentCategoryIndex(data.current_category_index);
      setTotalCategories(data.total_categories);
      setQuickActions(data.quick_actions || []);
      setSuggestedQuestions(data.suggested_questions || []);
      setStatus(data.status);
      
      // Initial AI message
      setMessages([{
        role: 'assistant',
        content: data.ai_message,
        timestamp: new Date()
      }]);
      
    } catch (error) {
      console.error('Start wizard error:', error);
      toast.error('Fehler beim Starten des Wizards: ' + error.message);
      
      // Fallback: Zeige eine Willkommensnachricht basierend auf den Kategorien
      if (categories && categories.length > 0) {
        const firstCat = categories[0];
        setTotalCategories(categories.length);
        setCurrentCategoryIndex(0);
        setStatus('in_progress');
        setMessages([{
          role: 'assistant',
          content: `🧙 **Willkommen zum Produkt-Setup-Wizard!**\n\nIch habe **${categories.length} Kategorien** mit insgesamt **${categories.reduce((sum, c) => sum + (c.products?.length || 0), 0)} Produkten** gefunden.\n\n## 📦 Kategorie: **${firstCat.name}** (${firstCat.products?.length || 0} Produkte)\n\nSie können mir in natürlicher Sprache sagen, was Sie ändern möchten:\n- *"Die Burger gibt es auch als Menü für 5,90€ extra"*\n- *"Extra Käse kostet 1,50€"*\n- *"Bei Salaten kann man Dressing wählen"*\n\n💡 Oder schreiben Sie **"weiter"** um zur nächsten Kategorie zu springen.`,
          timestamp: new Date()
        }]);
      }
    } finally {
      setIsStarting(false);
    }
  }, [extractionId, sessionToken, categories]);

  useEffect(() => {
    if (extractionId) {
      startWizard();
    }
  }, [extractionId, startWizard]);

  // Send message
  const sendMessage = async (messageText) => {
    if (!messageText.trim() || !wizardSessionId) return;
    
    const userMessage = messageText.trim();
    setInputValue('');
    setIsLoading(true);
    
    // Add user message immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/menu-ai/category-wizard/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          extraction_id: extractionId,
          wizard_session_id: wizardSessionId,
          message: userMessage
        })
      });
      
      if (!response.ok) {
        throw new Error('Chat-Fehler');
      }
      
      const data = await response.json();
      
      // Update state
      setCurrentCategoryIndex(data.current_category_index);
      setQuickActions(data.quick_actions || []);
      setSuggestedQuestions(data.suggested_questions || []);
      setStatus(data.status);
      
      if (data.category_config) {
        setCategoryConfigs(prev => ({
          ...prev,
          [data.current_category]: data.category_config
        }));
      }
      
      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.ai_message,
        changes: data.changes_made || [],
        timestamp: new Date()
      }]);
      
      // Check if completed
      if (data.status === 'completed') {
        toast.success('Wizard abgeschlossen! 🎉');
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Fehler beim Senden der Nachricht');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Es gab einen Fehler. Bitte versuchen Sie es erneut.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle quick action
  const handleQuickAction = (action) => {
    // Send as a structured message
    const actionText = `[AKTION: ${action.action}] ${action.label}`;
    sendMessage(actionText);
  };

  // Handle suggested question click
  const handleSuggestedQuestion = (question) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  // Apply configuration
  const applyConfiguration = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/menu-ai/category-wizard/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ extraction_id: extractionId })
      });
      
      if (!response.ok) {
        throw new Error('Konfiguration konnte nicht angewendet werden');
      }
      
      const data = await response.json();
      toast.success('Konfiguration erfolgreich angewendet! ✅');
      
      if (onComplete) {
        onComplete(data.categories);
      }
      
    } catch (error) {
      console.error('Apply config error:', error);
      toast.error('Fehler beim Anwenden der Konfiguration');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // Progress calculation
  const progress = totalCategories > 0 
    ? Math.round((currentCategoryIndex / totalCategories) * 100) 
    : 0;

  // Current category info
  const currentCategory = categories?.[currentCategoryIndex];
  const CategoryIcon = currentCategory ? getCategoryIcon(currentCategory.name) : Package;

  if (isStarting) {
    return (
      <Card className="bg-slate-800/90 border-slate-700 min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-400" />
          <p className="text-slate-300">Wizard wird gestartet...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/90 border-slate-700 flex flex-col h-[700px] max-h-[80vh]" data-testid="product-setup-wizard">
      {/* Header */}
      <CardHeader className="border-b border-slate-700 py-3 px-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/30">
              <Wand2 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-white text-lg">Produkt-Setup-Wizard</CardTitle>
              <p className="text-sm text-slate-400">
                {status === 'completed' 
                  ? 'Konfiguration abgeschlossen' 
                  : `Kategorie ${currentCategoryIndex + 1} von ${totalCategories}`
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {status === 'completed' && (
              <Button
                onClick={applyConfiguration}
                disabled={isLoading}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Konfiguration anwenden
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Fortschritt</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-700" />
        </div>
        
        {/* Current Category Badge */}
        {currentCategory && status !== 'completed' && (
          <div className="mt-3 flex items-center gap-2">
            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
              <CategoryIcon className="w-3 h-3 mr-1" />
              {currentCategory.name}
            </Badge>
            <span className="text-xs text-slate-500">
              ({currentCategory.products?.length || 0} Produkte)
            </span>
          </div>
        )}
      </CardHeader>
      
      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-slate-700 text-slate-100 rounded-bl-md'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({node, ...props}) => <h3 className="text-lg font-bold text-white mt-2 mb-2" {...props} />,
                        h2: ({node, ...props}) => <h4 className="text-base font-semibold text-purple-300 mt-3 mb-2" {...props} />,
                        h3: ({node, ...props}) => <h5 className="text-sm font-semibold text-teal-300 mt-2 mb-1" {...props} />,
                        p: ({node, ...props}) => <p className="text-slate-200 mb-2 text-sm leading-relaxed" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-1 mb-2" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-1 mb-2" {...props} />,
                        li: ({node, ...props}) => <li className="text-slate-300 text-sm" {...props} />,
                        strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                        em: ({node, ...props}) => <em className="text-slate-400" {...props} />,
                        hr: ({node, ...props}) => <hr className="border-slate-600 my-3" {...props} />,
                        code: ({node, ...props}) => <code className="bg-slate-800 px-1 py-0.5 rounded text-purple-300 text-xs" {...props} />,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
                
                {/* Changes Made */}
                {msg.changes && msg.changes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-600">
                    <p className="text-xs text-slate-400 mb-1">Änderungen:</p>
                    {msg.changes.map((change, i) => (
                      <p key={i} className="text-xs text-emerald-400">{change}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-700 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                  <span className="text-sm text-slate-400">KI denkt nach...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      
      {/* Quick Actions */}
      {quickActions.length > 0 && status !== 'completed' && (
        <div className="px-4 py-2 border-t border-slate-700 flex-shrink-0">
          <p className="text-xs text-slate-400 mb-2">Schnellaktionen:</p>
          <div className="flex flex-wrap gap-2">
            {quickActions.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* Suggested Questions */}
      {suggestedQuestions.length > 0 && status !== 'completed' && (
        <div className="px-4 py-2 border-t border-slate-700 flex-shrink-0">
          <p className="text-xs text-slate-400 mb-2">Vorgeschlagene Antworten:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.slice(0, 3).map((question, idx) => (
              <Button
                key={idx}
                variant="ghost"
                size="sm"
                onClick={() => handleSuggestedQuestion(question)}
                disabled={isLoading}
                className="text-slate-300 hover:bg-slate-700 text-xs text-left h-auto py-1.5 px-2"
              >
                {question.length > 50 ? question.slice(0, 50) + '...' : question}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* Input Area */}
      {status !== 'completed' && (
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ihre Antwort eingeben... (z.B. 'Ja, wir haben Menüs für 5,90€')"
              disabled={isLoading}
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              data-testid="wizard-input"
            />
            <Button
              onClick={() => sendMessage(inputValue)}
              disabled={isLoading || !inputValue.trim()}
              className="bg-purple-600 hover:bg-purple-500 text-white px-4"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => sendMessage('zurück')}
              disabled={isLoading || currentCategoryIndex === 0}
              className="text-slate-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Vorherige Kategorie
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => sendMessage('weiter')}
              disabled={isLoading}
              className="text-slate-400 hover:text-white"
            >
              {currentCategoryIndex >= totalCategories - 1 ? 'Abschließen' : 'Nächste Kategorie'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
